/**
 * La demande d'ACRE des micro-entrepreneurs, formulaire de l'Urssaf.
 *
 * Deuxième imprimé officiel de la rubrique après le Cerfa de CFE, et un cas
 * bien plus simple : deux pages, des champs nommés en clair, onze situations à
 * cocher avec la pièce à joindre écrite en face. L'imprimé vit dans
 * public/formulaires (référence NAT. 6843/02.2026/FORM, produit le 6 janvier
 * 2026 par l'Urssaf), et ce module dit quelle réponse va dans quelle case.
 *
 * CE QUE LE PARCOURS DOIT SAVOIR AVANT DE REMPLIR QUOI QUE CE SOIT :
 *   - la demande est obligatoire depuis le 1er janvier 2026, et se transmet
 *     dans les 60 jours du début d'activité déclaré au guichet unique. Passé ce
 *     délai, l'aide est perdue : remplir un formulaire hors délai n'aide
 *     personne, on le dit ;
 *   - une seule situation suffit, elles ne se cumulent pas, et chacune exige
 *     sa pièce justificative. Sans situation, pas de droit : on renvoie au
 *     simulateur, qui explique pourquoi ;
 *   - l'attestation sur l'honneur du bas de page engage sur l'absence d'ACRE
 *     dans les trois années précédentes. Qui en a bénéficié ne peut pas signer.
 *
 * Le numéro de sécurité sociale est la donnée la plus sensible que la rubrique
 * manipule. Il suit la même règle que tout le reste : composé dans le
 * navigateur, jamais envoyé, jamais conservé. Sa clé est contrôlée avant le
 * remplissage, parce qu'un numéro faux fait rejeter la demande, et qu'à ce
 * moment-là les 60 jours ont souvent passé.
 */

import { delaiDemandeOuvert, reprisePrecoce } from "./incomeTax.js";
import { dateEnToutesLettres, identite, nettoyer, nomDeFamille } from "./documentText.js";

/** L'imprimé embarqué, et ce qu'il vaut. */
export const MILLESIME = {
    annee: 2026,
    reference: "NAT. 6843/02.2026/FORM",
    fichier: "/formulaires/demande-acre-2026.pdf",
    /** Vérifié le 1er juillet 2026 sur la fiche officielle de l'Acre. */
    delaiJours: 60,
    reponseJours: 30,
};

/**
 * Les onze situations du tableau, dans l'ordre de l'imprimé.
 *
 * `case` est le nom exact de la case à cocher, `piece` la pièce justificative
 * telle que l'imprimé l'écrit en face. Les libellés courts sont ceux de nos
 * écrans ; le récapitulatif reprend le libellé de l'imprimé mot pour mot.
 */
export const SITUATIONS = [
    {
        cle: "demandeurIndemnise",
        case: "Situation 1",
        imprime: "Demandeur d'emploi indemnisé.",
        piece: "Notification d'ouverture de droit ou dernier titre de paiement.",
    },
    {
        cle: "demandeurNonIndemnise",
        case: "Situation 2",
        imprime: "Demandeur d'emploi non indemnisé inscrit à France Travail six mois au cours des dix-huit derniers mois.",
        piece: "Historique de l'inscription à France Travail.",
    },
    {
        cle: "rsaAss",
        case: "Situation 3",
        imprime: "Bénéficiaire du Revenu de solidarité active (RSA), ou de l'Allocation de solidarité spécifique (ASS).",
        piece: "Attestation justifiant la qualité d'allocataire ou de bénéficiaire des aides mentionnées.",
    },
    {
        cle: "jeune",
        case: "Situation 4",
        imprime: "Jeune de 18 à 25 ans révolus.",
        piece: "Pièce d'identité attestant de la date de naissance.",
    },
    {
        cle: "moinsDeTrenteNonIndemnise",
        case: "Situation 5",
        imprime: "Personne de moins de 30 ans non indemnisée (durée d'activité insuffisante pour l'ouverture de droits).",
        piece: "Pièce d'identité attestant de la date de naissance et attestation sur l'honneur de non indemnisation par le régime d'assurance chômage ou contrat de travail accompagné de toute pièce attestant de sa rupture.",
    },
    {
        cle: "moinsDeTrenteHandicape",
        case: "Situation 6",
        imprime: "Personne de moins de 30 ans reconnue handicapée.",
        piece: "Pièce d'identité attestant de la date de naissance et justificatif de reconnaissance de personne handicapée délivrée par la commission départementale des droits et de l'autonomie.",
    },
    {
        cle: "repriseEntrepriseEnDifficulte",
        case: "Situation 7",
        imprime: "Salarié ou personne licenciée d'une entreprise en sauvegarde, redressement ou en liquidation judiciaire qui reprend l'activité de l'entreprise.",
        piece: "Copie du jugement d'ouverture de la procédure de liquidation judiciaire ou à défaut une attestation du liquidateur, de l'administrateur judiciaire ou du juge commissaire.",
    },
    {
        cle: "cape",
        case: "Situation 8",
        imprime: "Personne ayant conclu un Cape (contrat d'appui au projet d'entreprise), sous réserve qu'elle remplisse l'une des sept conditions prévues ci-dessus à la date de conclusion de ce contrat.",
        piece: "Copie du contrat d'appui au projet d'entreprise (Cape).",
    },
    {
        cle: "qpv",
        case: "Situation 9",
        imprime: "Personne créant une entreprise implantée au sein d'un quartier prioritaire de la politique de la ville (QPV).",
        piece: "Justification de l'adresse de l'établissement où s'exerce l'activité dans le QPV.",
    },
    {
        cle: "prepare",
        case: "Situation 10",
        imprime: "Bénéficiaire de la Prestation partagée d'éducation de l'enfant (PrePare).",
        piece: "Notification d'ouverture du droit à la prestation ou titre du dernier paiement.",
    },
    {
        cle: "zfrr",
        case: "Situation 11",
        imprime: "Exercice de l'activité au sein d'une zone France ruralités revitalisation (ZFRR) ou d'une zone France ruralités revitalisation « plus » (ZFRR+).",
        piece: "Justification de l'adresse de l'établissement où s'exerce l'activité dans la ZFRR ou ZFRR+.",
    },
];

export const situationParCle = (cle) => SITUATIONS.find((s) => s.cle === cle) || null;

/**
 * La clé d'un numéro de sécurité sociale : 97 moins le reste de la division
 * des treize premiers chiffres par 97, la Corse comptant 19 pour 2A et 18
 * pour 2B, comme le fait l'Insee.
 */
export function cleNir(treize) {
    const corrige = treize.replace(/^(\d{5})2A/, "$119").replace(/^(\d{5})2B/, "$118");
    if (!/^\d{13}$/.test(corrige)) return null;
    return String(97 - (Number(BigInt(corrige) % 97n))).padStart(2, "0");
}

/**
 * Le numéro de sécurité sociale, normalisé et jugé.
 *
 * Rend `{ valeur, motif }` : `valeur` est le numéro à poser sur l'imprimé
 * (treize ou quinze caractères), `motif` nomme le refus s'il y en a un.
 */
export function nir(brut) {
    const n = nettoyer(brut).replace(/\s/g, "").toUpperCase();
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^[12][0-9]{4}(\d{2}|2A|2B)\d{6}(\d{2})?$/.test(n)) return { valeur: n, motif: "format" };
    if (n.length === 15 && cleNir(n.slice(0, 13)) !== n.slice(13)) return { valeur: n, motif: "cle" };
    return { valeur: n, motif: null };
}

/**
 * Qui peut déposer cette demande, et qui doit s'arrêter avant.
 *
 * Cinq sorties, dans l'ordre où le parcours les pose : l'activité déclarée, le
 * délai des 60 jours, l'ACRE dans les trois ans, la reprise trop proche d'une
 * cessation, la situation.
 *
 * LES DEUX RÈGLES DE REPRISE NE SE RECOUVRENT PAS. Le délai de trois ans de
 * l'article L. 131-6-4, IV interdit une deuxième ACRE. L'article R. 131-3 dit
 * autre chose : une reprise intervenue dans l'année de la cessation ou dans
 * l'année suivante n'est pas un début d'activité, et l'ACRE n'est due qu'à un
 * début d'activité. Elle se refuse donc aussi à qui n'en a jamais bénéficié.
 */
export function concerne(reponses, aujourdhui) {
    const r = reponses || {};
    if (r.declare === "non") return { concerne: false, motif: "pasDeclare" };
    if (r.declare !== "oui") return { concerne: null, motif: null };

    if (dateEnToutesLettres(r.dateDebut) && aujourdhui) {
        const { ouvert } = delaiDemandeOuvert(
            new Date(`${r.dateDebut}T00:00:00Z`),
            new Date(aujourdhui),
        );
        if (!ouvert) return { concerne: false, motif: "horsDelai" };
    }
    if (r.dejaBeneficie === "oui") return { concerne: false, motif: "dejaBeneficie" };
    if (reprisePrecoce(r.dateCessation ? `${r.dateCessation}T00:00:00Z` : null,
        r.dateDebut ? `${r.dateDebut}T00:00:00Z` : null).reprise) {
        return { concerne: false, motif: "reprisePrecoce" };
    }
    if (r.situation === "aucune") return { concerne: false, motif: "aucuneSituation" };
    return { concerne: true, motif: null };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r, aujourdhui).concerne) {
        return { valide: false, manques: ["declare"], invalides, alertes };
    }

    if (!situationParCle(r.situation)) manques.push("situation");
    if (r.sexe !== "M" && r.sexe !== "F") manques.push("sexe");

    const requis = [
        ["nomNaissance", r.nomNaissance],
        ["prenoms", r.prenoms],
        ["nationalite", r.nationalite],
        ["mail", r.mail],
        ["adresse", r.adresse],
        ["ville", r.ville],
        ["pays", r.pays],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    if (nettoyer(r.mail) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nettoyer(r.mail))) {
        invalides.push({ champ: "mail", motif: "format" });
    }

    const cp = nettoyer(r.cp);
    if (!cp) manques.push("cp");
    else if (!/^\d{5}$/.test(cp)) invalides.push({ champ: "cp", motif: "format" });

    const numero = nir(r.nni);
    if (numero.motif === "vide") manques.push("nni");
    else if (numero.motif) invalides.push({ champ: "nni", motif: numero.motif });
    // Le premier chiffre dit le sexe : 1 pour un homme, 2 pour une femme. Un
    // désaccord avec la case cochée est presque toujours une faute de frappe.
    else if ((numero.valeur[0] === "1" && r.sexe === "F") || (numero.valeur[0] === "2" && r.sexe === "M")) {
        alertes.push("nirEtSexe");
    }

    for (const cle of ["dateDebut", "dateNaissance", "dateSignature"]) {
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!dateEnToutesLettres(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    if (dateEnToutesLettres(r.dateNaissance) && aujourdhui) {
        const ans =
            (new Date(aujourdhui) - new Date(`${r.dateNaissance}T00:00:00Z`)) /
            (365.2425 * 24 * 3600 * 1000);
        if (ans < 0 || ans > 110) invalides.push({ champ: "dateNaissance", motif: "ageImpossible" });
        // Les situations liées à l'âge se vérifient ici : l'Urssaf le fera.
        else if (r.situation === "jeune" && (ans < 18 || ans >= 26)) alertes.push("ageJeune");
        else if (
            (r.situation === "moinsDeTrenteNonIndemnise" || r.situation === "moinsDeTrenteHandicape") &&
            ans >= 30
        ) {
            alertes.push("ageTrente");
        }
    }

    if (r.ambulant) {
        if (!nettoyer(r.communeActivite)) manques.push("communeActivite");
        const cpa = nettoyer(r.cpActivite);
        if (!cpa) manques.push("cpActivite");
        else if (!/^\d{5}$/.test(cpa)) invalides.push({ champ: "cpActivite", motif: "format" });
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** Une date ISO en JJ/MM/AAAA, le format que l'imprimé demande. */
export function jourMoisAnnee(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * La projection des réponses sur les champs de l'imprimé.
 *
 * Les noms sont ceux du formulaire de l'Urssaf, en clair : « NOM de
 * naissance », « Situation 4 »... La zone de signature est un champ de
 * signature numérique que l'on ne remplit pas ; l'image du paraphe se dessine
 * dans son rectangle, en haut à gauche, à une taille de signature et non de
 * bannière.
 */
export function champsDuFormulaire(reponses, aujourdhui) {
    const r = reponses || {};
    if (!valider(r, aujourdhui).valide) return null;
    const situation = situationParCle(r.situation);

    const textes = {
        "NOM de naissance": nomDeFamille(r.nomNaissance),
        "NOM d'usage": nomDeFamille(r.nomUsage),
        PRENOMS: nettoyer(r.prenoms),
        Nationalité: nettoyer(r.nationalite),
        "DATE de naissance": jourMoisAnnee(r.dateNaissance),
        NNI: nir(r.nni).valeur,
        "NUM TEL": nettoyer(r.telephone).replace(/\s/g, ""),
        "Adresse MAIL": nettoyer(r.mail),
        "Adresse domicile": nettoyer(r.adresse),
        VILLE: nettoyer(r.ville),
        CP: nettoyer(r.cp),
        PAYS: nettoyer(r.pays),
        "DATE signature": jourMoisAnnee(r.dateSignature),
    };
    if (r.ambulant) {
        textes["Commune de l'activité principale"] = nettoyer(r.communeActivite);
        textes["CP activité principale"] = nettoyer(r.cpActivite);
    }

    const cases = [situation.case];
    if (r.ambulant) cases.push("Personne sans domicile stable (Ambulant)");
    if (r.mineurEmancipe) cases.push("Mineur émancipé autorisé à être commerçant par décision judiciaire");

    return {
        textes,
        cases,
        radios: { Sexe: r.sexe },
        // Le rectangle du champ « Signature du demandeur », page 2 : x 34,
        // y 122 à 223, 520 de large. Le paraphe s'y pose en haut à gauche.
        signature: { page: 2, x: 40, y: 160, largeur: 260, hauteur: 58 },
        millesime: MILLESIME,
    };
}

/** Le récapitulatif montré avant le téléchargement, en français lisible. */
export function recapitulatif(reponses, aujourdhui) {
    const r = reponses || {};
    if (!champsDuFormulaire(r, aujourdhui)) return null;
    const situation = situationParCle(r.situation);
    const { limite } = delaiDemandeOuvert(new Date(`${r.dateDebut}T00:00:00Z`), new Date(aujourdhui));

    return {
        lignes: [
            ["Demandeur", `${r.sexe === "F" ? "Mme" : "M."} ${identite({ prenoms: r.prenoms, nom: r.nomNaissance })}${nettoyer(r.nomUsage) ? `, nom d'usage ${nomDeFamille(r.nomUsage)}` : ""}`],
            ["Naissance", `${dateEnToutesLettres(r.dateNaissance)}, ${nettoyer(r.nationalite)}`],
            ["Numéro de sécurité sociale", nir(r.nni).valeur],
            ["Contact", [nettoyer(r.mail), nettoyer(r.telephone)].filter(Boolean).join(", ")],
            ["Domicile", `${nettoyer(r.adresse)}, ${nettoyer(r.cp)} ${nettoyer(r.ville)}, ${nettoyer(r.pays)}`],
            ...(r.ambulant ? [["Sans domicile stable, activité principale à", `${nettoyer(r.communeActivite)} (${nettoyer(r.cpActivite)})`]] : []),
            ...(r.mineurEmancipe ? [["Mineur émancipé", "case cochée"]] : []),
            ["Début d'activité déclaré", dateEnToutesLettres(r.dateDebut)],
            ["Demande à transmettre avant le", dateEnToutesLettres(limite.toISOString().slice(0, 10))],
            ["Situation cochée", situation.imprime],
            ["Attestation sur l'honneur", "aucune Acre accordée au cours des 3 dernières années"],
            ["Date de signature", dateEnToutesLettres(r.dateSignature)],
        ],
        pieces: [
            "Le justificatif de création d'activité, téléchargé sur le guichet unique une fois la déclaration finalisée.",
            situation.piece,
        ],
    };
}
